import { useEffect, useState } from "react";
import { ArrowTopRightOnSquareIcon } from "@heroicons/react/24/outline";

// The second listing's live Meetup and Eventbrite links, and the way back when
// one of them has been deleted on the site itself.
//
// Deleting a listing on Meetup or Eventbrite leaves its id on the event, so
// every later sync goes on editing something that is gone and nothing new is
// ever made. Unlinking is the fix: with no id, the next sync creates. Which
// ones is the author's call, because deleting one site's listing says nothing
// about the other, and unlinking a listing that is still up puts a second one
// beside it.
//
// A platform with no link gets a row of its own, so an unlink never leaves the
// author with nothing to press: "on its way" while a sync is queued or still
// running, otherwise a button that asks for one.

const PLATFORMS = ["meetup", "eventbrite"];
const PLATFORM_LABELS = { meetup: "Meetup", eventbrite: "Eventbrite" };

// A sync is queued for a fire time and then takes a couple of minutes to run,
// nine at the very worst (updateMeetupEvent's timeout). Until that has passed a
// missing listing may still be on its way, and asking for another would only
// queue a second sync behind the first.
const SYNC_RUN_GRACE_MS = 10 * 60 * 1000;

const NOTE_CLASS =
  "mt-3 rounded-md border border-yellow-200 dark:border-yellow-800 bg-yellow-50 dark:bg-yellow-900/30 p-2 text-sm text-yellow-800 dark:text-yellow-200";

const LINK_BUTTON_CLASS =
  "rounded text-xs font-medium text-primary hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-primary disabled:opacity-50 disabled:cursor-not-allowed";

const listNames = (platforms) =>
  platforms.map((p) => PLATFORM_LABELS[p]).join(" and ");

const listingNoun = (platforms) => (platforms.length > 1 ? "listings" : "listing");

const toMillis = (value) => {
  if (!value) return 0;
  if (typeof value.toMillis === "function") return value.toMillis();
  if (value instanceof Date) return value.getTime();
  return typeof value === "number" ? value : 0;
};

/** What happened, in the words the author needs, from the callable's answer. */
export const recreateNotice = (result, platforms) => {
  // What the server actually cleared, which is nothing when the author asked
  // for a listing that had no link to begin with.
  const unlinked = Array.isArray(result?.unlinked) ? result.unlinked : platforms;
  const cleared = unlinked.length
    ? `Unlinked the old ${listNames(unlinked)} ${listingNoun(unlinked)}. `
    : "";
  if (result?.scheduled) {
    const coming = platforms.length > 1
      ? `New ${listNames(platforms)} listings go up in a few minutes.`
      : `A new ${listNames(platforms)} listing goes up in a few minutes.`;
    const draft = platforms.includes("meetup")
      ? " The Meetup one arrives as a draft and the host gets a text to publish it."
      : "";
    return `${cleared}${coming}${draft}`;
  }
  const why = {
    cancelled: "This event is cancelled, so nothing new goes up.",
    finished: "This event is over, so nothing new goes up.",
    inactive: "This event is not active, so nothing new goes up until it is.",
    // The server judges the SAVED listing, which can differ from the form.
    switched_off: "Nothing new goes up until this listing is switched on and saved.",
  }[result?.skipReason];
  return `${cleared}${why || "Nothing new goes up while this listing is switched off."}`;
};

const PublishedListings = ({
  meetupUrl,
  eventbriteUrl,
  willPublish = false,
  // When this listing's next sync fires (listingSyncState.<id>.meetupTaskScheduledFor).
  syncScheduledFor = null,
  // Present once this listing has been through the sync at least once. A listing
  // that never has is not "missing" anything yet; its first save makes it.
  everSynced = false,
  // Platforms the event's own listing is on. One the event never reached (no
  // Meetup group for the region, a host with no Meetup account) is not offered,
  // or the row would sit there forever with a button that makes nothing.
  platformsInUse = PLATFORMS,
  onRecreate,
  recreating = false,
}) => {
  const [choosing, setChoosing] = useState(false);
  const [picked, setPicked] = useState([]);
  const [notice, setNotice] = useState(null);
  const [now, setNow] = useState(() => Date.now());

  const fireAt = toMillis(syncScheduledFor);
  const syncPending = fireAt > 0 && now < fireAt + SYNC_RUN_GRACE_MS;

  // Nothing tells the panel a sync has finished without writing an id (a failed
  // create writes nothing), so wake up once the window closes and offer the
  // button again.
  useEffect(() => {
    if (!syncPending) return undefined;
    // Measured from the real clock: `now` only moves when this timer fires, so
    // on a panel left open a while it is older than it looks.
    const wait = Math.max(fireAt + SYNC_RUN_GRACE_MS - Date.now() + 1000, 0);
    const timer = setTimeout(() => setNow(Date.now()), wait);
    return () => clearTimeout(timer);
  }, [syncPending, fireAt]);

  const links = [
    meetupUrl && { platform: "meetup", url: meetupUrl },
    eventbriteUrl && { platform: "eventbrite", url: eventbriteUrl },
  ].filter(Boolean);
  const linked = links.map((l) => l.platform);

  const missing = PLATFORMS.filter(
    (p) => !linked.includes(p) && platformsInUse.includes(p)
  );
  const showMissing = missing.length > 0 && (links.length > 0 || everSynced);

  // A pick whose link has since gone (the event stream caught up) is not a pick.
  const chosen = picked.filter((p) => linked.includes(p));

  // Rendered either way by the section: the notice has to outlive the links it
  // just unlinked, and those vanish as soon as the event doc updates.
  if (links.length === 0 && !showMissing && !notice) return null;

  const toggle = (platform) =>
    setPicked((current) =>
      current.includes(platform)
        ? current.filter((p) => p !== platform)
        : [...current, platform]
    );

  const close = () => {
    setChoosing(false);
    setPicked([]);
  };

  const submit = async () => {
    if (chosen.length === 0 || recreating) return;
    const result = await onRecreate?.(chosen, { republish: willPublish });
    // A failure has already said so; keep the picks so trying again is one click.
    if (!result) return;
    close();
    setNotice(recreateNotice(result, chosen));
  };

  // Nothing is linked on this platform, so there is nothing to unlink and no
  // duplicate to warn about: this only queues the sync that makes one.
  const make = async (platform) => {
    if (recreating) return;
    const result = await onRecreate?.([platform], { republish: true });
    if (!result) return;
    setNotice(recreateNotice(result, [platform]));
  };

  return (
    <>
      {(links.length > 0 || showMissing) && (
        <div className="mt-3 border-t border-stone-200 dark:border-stone-700 pt-3">
          <p className="text-xs font-medium text-gray-500 dark:text-gray-400">
            Published listings
          </p>
          {links.length > 0 && (
            <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs">
              {links.map(({ platform, url }) => (
                <a
                  key={platform}
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-primary hover:underline"
                >
                  {PLATFORM_LABELS[platform]}
                  <ArrowTopRightOnSquareIcon className="h-3 w-3" aria-hidden="true" />
                </a>
              ))}
            </div>
          )}

          {showMissing && (
            <div className="mt-1 space-y-1 text-xs text-gray-500 dark:text-gray-400">
              {syncPending ? (
                <p>
                  {missing.length > 1
                    ? `New ${listNames(missing)} listings are on their way.`
                    : `A new ${listNames(missing)} listing is on its way.`}{" "}
                  It usually takes a few minutes.
                </p>
              ) : willPublish ? (
                missing.map((platform) => (
                  <p key={platform} className="flex flex-wrap items-center gap-x-2">
                    <span>Not on {PLATFORM_LABELS[platform]} right now.</span>
                    <button
                      type="button"
                      onClick={() => make(platform)}
                      disabled={recreating}
                      className={LINK_BUTTON_CLASS}
                    >
                      {recreating ? "Working..." : `Make a new ${PLATFORM_LABELS[platform]} listing`}
                    </button>
                  </p>
                ))
              ) : (
                <p>
                  Not on {listNames(missing)} right now. Switch this listing on to make
                  one.
                </p>
              )}
            </div>
          )}

          {links.length > 0 && !choosing && (
            <button
              type="button"
              onClick={() => {
                setChoosing(true);
                setNotice(null);
              }}
              className="mt-2 rounded text-xs font-medium text-gray-400 dark:text-gray-500 hover:text-primary transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            >
              {willPublish
                ? "Deleted one of these? Make a new one"
                : "Deleted one of these? Unlink it"}
            </button>
          )}

          {links.length > 0 && choosing && (
            <fieldset className="mt-2 rounded-md bg-white dark:bg-gray-800/40 ring-1 ring-gray-200 dark:ring-gray-700 p-3">
              <legend className="sr-only">Listings to make again</legend>
              <p className="text-xs font-medium text-gray-500 dark:text-gray-400">
                Which ones did you delete?
              </p>
              <div className="mt-2 space-y-1.5">
                {links.map(({ platform }) => (
                  <label
                    key={platform}
                    className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-200"
                  >
                    <input
                      type="checkbox"
                      checked={chosen.includes(platform)}
                      onChange={() => toggle(platform)}
                      className="h-4 w-4 rounded border-gray-300 text-primary dark:border-gray-600"
                    />
                    {PLATFORM_LABELS[platform]}
                  </label>
                ))}
              </div>

              {/* Stays in plain sight: getting this wrong is the duplicate listing. */}
              <p className={NOTE_CLASS}>
                Only pick a listing you already deleted on that site. If it is still
                up, you will end up with two.
              </p>
              <p className="mt-2 text-xs text-gray-400 dark:text-gray-500">
                {willPublish
                  ? "A new one is made from the last saved version of this listing, usually within a few minutes."
                  : "This listing is switched off, so nothing new is made. The old one is only unlinked from this event."}
              </p>

              <div className="mt-3 flex items-center gap-3">
                <button
                  type="button"
                  onClick={submit}
                  disabled={chosen.length === 0 || recreating}
                  className={`rounded border border-primary bg-white dark:bg-gray-800 px-4 py-2 text-sm text-primary focus:outline-none focus-visible:ring-2 focus-visible:ring-primary ${
                    recreating ? "animate-pulse" : "disabled:opacity-50 disabled:cursor-not-allowed"
                  }`}
                >
                  {recreating ? "Working..." : willPublish ? "Make new listing" : "Unlink"}
                </button>
                <button
                  type="button"
                  onClick={close}
                  disabled={recreating}
                  className="rounded text-xs font-medium text-gray-400 dark:text-gray-500 hover:text-primary transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                >
                  Cancel
                </button>
              </div>
            </fieldset>
          )}
        </div>
      )}

      <div aria-live="polite">
        {notice && (
          <p className="mt-3 text-xs text-gray-500 dark:text-gray-400">{notice}</p>
        )}
      </div>
    </>
  );
};

export default PublishedListings;
